import {
    AppAvatar,
    AppBox,
    AppButton,
    AppIconButton,
    AppLink,
    AppModal,
    AppPaper,
    AppSelect,
    AppText,
    AppTitle,
} from "../../../../commons/components";
import AppBorderBox from "../../../../commons/components/BorderBox";
import IconEmpty from "../../../../commons/components/icons/empty";
import {ISelectOptions} from "../../../../commons/components/ui-components/Select";
import IconApproval from "../../../../commons/components/icons/approval";
import {useAppDispatch, useAppSelector} from "../../../../hooks/app";
import {StatusEnum} from "../../../../commons/enums/status-enum";
import SkeletonGlobalCardList from "../../../../commons/components/skeleton/SkeletonGlobalCardList";
import {UserStatusEnum} from "../../../../commons/enums/user-status-enum";
import {useEffect, useState} from "react";
import IconCheckMarkCircle from "../../../../commons/components/icons/checkmark-circle";
import {toTitleCase,} from "../../../../commons/utilities/string-utils";
import {findIndexByCondition} from "../../../../commons/utilities/array-utils";
import {useSnackbar} from "../../../../redux/snackbarProvider";
import setDocumentTitle from "../../../../commons/utilities/document-title";
import {AxiosError} from "axios";
import {IServerErrorResponse} from "../../../../commons/components/interface";
import IconPersonAdd from "../../../../commons/components/icons/person-add";
import IconEdit from "../../../../commons/components/icons/edit";
import {cleanUpStepperFormData} from "../../../../redux/slices/stepper-form-slice";
import ability, {Can} from "../../../../casl/Can";
import {RolePolicyActionEnum} from "../../../../commons/enums/role-policy-action.enum";
import {User} from "../../../../commons/models/user";
import IconProhibited from "../../../../commons/components/icons/prohibited";
import {fetchManageUserList} from "../../../../redux/slices/user/manage-user-list-slice";
import UserAddEdit from "./userAddEdit/UserAddEdit";
import UserDeactive from "./UserDeactive";
import UserActive from "./UserActive";
import {userStatusChange} from "../../../../redux/slices/user/manage-user-edit-slice";
import {updateUser} from "../../../../redux/slices/session-slice";
import FilterToolbar from "../../../../commons/components/ui-components/FilterToolbar";
import useFilter from "../../../../hooks/useFilter";

export default function UserList() {
    const dispatch = useAppDispatch();
    const addSnackbar = useSnackbar();
    const {user: loggedUser} = useAppSelector((state) => state.session);
    const {userList, userListStatus} = useAppSelector(
        (state) => state.manageUserList
    );

    const {userToggleStatus, userEditResponseStatus} = useAppSelector((state) => state.manageUserEdit);
    const { userAddResponseStatus } = useAppSelector((state) => state.manageUserAdd);

    const [isDeactiveUser, setDeactiveUser] = useState(false);
    const [isActivateUser, setActivateUser] = useState(false);
    const [isAddEditUser, setAddEditUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const {status, sortBy, sortOrder, searchText} = useFilter();
    const userStatusOptions: ISelectOptions[] = [
        {
            title: "Active",
            icon: <IconApproval color="var(--fg-outline-success)"/>,
            value: UserStatusEnum.Active.toString(),
        },
        {
            title: "Deactive",
            icon: <IconProhibited color="var(--fg-outline-danger)"/>,
            value: UserStatusEnum.Deactive.toString(),
        },
    ];

    const fetchUserList = () => {
        dispatch(fetchManageUserList({sort: sortBy, sortOrder, status, name: searchText}));
    }

    const handleEditClick = (user: User) => (event: any) => {
        setSelectedUser(user);
        setAddEditUser(true);
    };

    const handleAfterAddEdit = (updatedUser?: User) => {
        setSelectedUser(null);
        setAddEditUser(false);
        if (updatedUser) {
            dispatch(updateUser(updatedUser))
        }
        fetchUserList();
    };

    const handleAfterAddEditClose = () => {
        setSelectedUser(null);
        setAddEditUser(false);
        dispatch(cleanUpStepperFormData());
    };

    useEffect(() => {
        if (loggedUser) {
            const matchUser = userList.first(item => loggedUser.id === item.id);
            if (matchUser) {
                Object.assign(matchUser, {
                    firstName: loggedUser.firstName,
                    avatar: loggedUser.avatar,
                    fullName: loggedUser.fullName
                });
            }
        }

    }, [loggedUser]);

    const handleStatusChange = (value: string | number, user: User) => {
        if (value === UserStatusEnum.Deactive.toString()) {
            setSelectedUser(user);
            setDeactiveUser(true);
        } else {
            setSelectedUser(user);
            setActivateUser(true);
        }
    };

    const handleDeactiveConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedUser) {
            dispatch(userStatusChange({userId: selectedUser.id, status: UserStatusEnum.Deactive}))
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "user-retire-success",
                        text: "User Deactived Successfully",
                        variant: "success",
                    });
                    setDeactiveUser(false);
                    setSelectedUser(null);
                    fetchUserList();
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
        }
    };

    const handleActiveConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedUser) {
            dispatch(userStatusChange({userId: selectedUser.id, status: UserStatusEnum.Active}))
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "user-active-success",
                        text: "User Activated Successfully",
                        variant: "success",
                    });
                    setActivateUser(false);
                    setSelectedUser(null);
                    fetchUserList();
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
        }
    };

    const handleClick = () => {
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        setDocumentTitle(["Users", "Manage"]);
    }, []);
    return (
        <>
            <AppBox flexDirection="column" gap="xs">
                <AppBox alignItems="center" justifyContent="space-between">
                    <AppTitle as="h4">All Users</AppTitle>
                    <Can I={RolePolicyActionEnum.CREATE} a={User}>
                        {userList.length > 0 && (
                            <AppButton
                                label="Add User"
                                onClick={() => setAddEditUser(true)}
                            />
                        )}
                    </Can>
                </AppBox>
                <FilterToolbar/>
                {userListStatus === StatusEnum.Loading ? (
                    <SkeletonGlobalCardList
                        noTitle
                        numberOfItems={10}
                        withAvatar
                        withBadge
                    />
                ) : (
                    <>
                        {userList.all().length === 0 ? (
                            <AppPaper style={{height: "100%"}}>
                                <AppBorderBox borderVariant="dashed">
                                    <AppBox
                                        flexDirection="column"
                                        alignItems="center"
                                        gap="sm"
                                        justifyContent="center"
                                        style={{height: "35rem"}}
                                    >
                                        <AppBox
                                            alignItems="center"
                                            flexDirection="column"
                                            justifyContent="center"
                                            gap="2xs"
                                        >
                                            <IconEmpty
                                                width={56}
                                                height={56}
                                                color="rgba(var(--border-200))"
                                            />
                                            <AppTitle as="h6">No Users Found</AppTitle>
                                            <Can I={RolePolicyActionEnum.CREATE} a={User}>
                                                <AppText>
                                                    Ready to kick things off? Let's get started by adding
                                                    users.
                                                </AppText>
                                            </Can>
                                        </AppBox>
                                        <Can I={RolePolicyActionEnum.CREATE} a={User}>
                                            <AppButton
                                                label="Add New User"
                                                onClick={() => setAddEditUser(true)}
                                            />
                                        </Can>
                                    </AppBox>
                                </AppBorderBox>
                            </AppPaper>
                        ) : (
                            <>
                                {userList.all().map((user, index) => (
                                    <AppPaper radius="sm" padding="none" shadow="xs" key={index}>
                                        <AppBox
                                            gap="xs"
                                            my="2xs"
                                            mx="xs"
                                            justifyContent="space-between"
                                        >
                                            <AppLink
                                                to={`/manage/users/${user.id}`}
                                                onClick={handleClick}
                                            >
                                                <AppBox gap="xs" alignItems="center">
                                                    <AppAvatar
                                                        username={user.fullName}
                                                        src={user.avatar}
                                                        size="lg"
                                                    />
                                                    <AppBox flexDirection="column">
                                                        <AppTitle as={"h5"}>
                                                            {toTitleCase(user.fullName)}
                                                        </AppTitle>
                                                    </AppBox>
                                                </AppBox>
                                            </AppLink>
                                            <AppBox gap="xs" justifyContent="end" alignItems="center">
                                                <AppSelect
                                                    options={userStatusOptions}
                                                    className="manage-dropdown"
                                                    optionsClassName="manage-dropdown-option"
                                                    disabled={ability.cannot(
                                                        RolePolicyActionEnum.STATUS,
                                                        user
                                                    ) || user.id === loggedUser?.id}
                                                    currentOption={
                                                        userStatusOptions[
                                                            findIndexByCondition(
                                                                userStatusOptions,
                                                                (option) =>
                                                                    option.value === user.status.toString()
                                                            )
                                                            ] ?? userStatusOptions[0]
                                                    }
                                                    onChange={(option) =>
                                                        handleStatusChange(option.value, user)
                                                    }
                                                />
                                                <Can I={RolePolicyActionEnum.UPDATE} this={user}>
                                                    <AppIconButton
                                                        icon={<IconEdit/>}
                                                        variant="light"
                                                        onClick={handleEditClick(user)}
                                                    />
                                                </Can>
                                            </AppBox>
                                        </AppBox>
                                    </AppPaper>
                                ))}
                            </>
                        )}
                    </>
                )}
            </AppBox>
            <AppModal
                opened={isAddEditUser}
                modalSize="lg"
                onClose={handleAfterAddEditClose}
                title={selectedUser ? "Edit User" : "Add New User"}
                titleIcon={selectedUser ? <IconEdit/> : <IconPersonAdd/>}
                withoutButtons
                withLoadingButton={
                    userEditResponseStatus === StatusEnum.Loading || userAddResponseStatus === StatusEnum.Loading
                }
            >
                <UserAddEdit
                    onSave={handleAfterAddEdit}
                    userData={selectedUser}
                    onCancel={handleAfterAddEditClose}
                />
            </AppModal>
            {selectedUser && (
                <>
                    <AppModal
                        opened={isDeactiveUser}
                        title="Want To Deactive This User?"
                        titleIcon={<IconProhibited/>}
                        iconColor="danger"
                        confirmButtonLabel="Deactive"
                        withLoadingButton={userToggleStatus === StatusEnum.Loading}
                        onClose={() => {
                            setDeactiveUser(false);
                        }}
                        onSave={(e) => {
                            handleDeactiveConfirm(e as Event);
                        }}
                    >
                        <UserDeactive user={selectedUser}/>
                    </AppModal>
                    <AppModal
                        opened={isActivateUser}
                        title="Want To Active This User?"
                        titleIcon={<IconCheckMarkCircle/>}
                        iconColor="success"
                        confirmButtonLabel="Active"
                        withLoadingButton={userToggleStatus === StatusEnum.Loading}
                        onClose={() => {
                            setActivateUser(false);
                        }}
                        onSave={(e) => {
                            handleActiveConfirm(e as Event);
                        }}
                    >
                        <UserActive user={selectedUser}/>
                    </AppModal>
                </>
            )}
        </>
    );
}
